import React from 'react';
import AdSlot from '../AdSlot';
import { renderToString } from 'react-dom/server';

export const injectHorizontalAds = (
  ads = [],
  contentElSelector = '.DescriptionSection',
  mainContainerElSelector = '#app',
  averageAdHeight = 150,
  featureDisplayAdsFlag,
  minimumAdSpacing = 500,
  parseSubChildWithClassNames = true
) => {
  const contentContainerEl = document.querySelector(contentElSelector);
  const mainContainerEl = document.querySelector(mainContainerElSelector);
  const validSectionTags = [
    'section',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'div'
  ];

  // get the height of these containers
  const contentContainerHeight = contentContainerEl.offsetHeight;
  const mainContainerHeight = mainContainerEl.offsetHeight;

  // ads won't show in the bottom area to avoid clashing with the bottom ad unit
  // currently it's calculated as the bottom 20% of the content container height
  // but this can also be set as a fixed height (e.g. for the bottom 600px, set the value to 600 instead)
  const bottomSafeArea = Math.floor(0.2 * contentContainerHeight);

  // figure out how many ads we can place, based on the number of ads we preferably want but limit to 30% of the main content height
  const maxNumberOfAds = Math.floor(
    (0.3 * mainContainerHeight) / averageAdHeight
  );
  const amountOfAdsToPlace =
    maxNumberOfAds > ads.length ? ads.length : maxNumberOfAds;
  const evenlyDistributedHeight = Math.max(
    Math.floor(
      (contentContainerHeight - bottomSafeArea) / (amountOfAdsToPlace + 1)
    ),
    minimumAdSpacing
  );

  // find all immediate children in the content container that potentially can be considered sections
  const immediateChildren = contentContainerEl.querySelectorAll(
    ':scope > div, :scope > section, :scope > span, :scope > p, :scope > ol, :scope > ul'
  );

  const outerHeight = el => {
    let height = el.offsetHeight;
    const style = window.getComputedStyle(el);
    return (height += parseInt(style.marginTop) + parseInt(style.marginBottom));
  };

  // let's set the height counter at 0
  let heightCounter = 0;
  let cumulativeHeight = 0;
  let amountOfAdsDisplayed = 0;

  const evaluateAdPlacement = el => {
    // when the heightcounter is above the evenly distributed height, we can place an ad and reset the counter to start over again while we're still in the loop
    let placeAd = false;
    if (
      heightCounter >= evenlyDistributedHeight &&
      amountOfAdsDisplayed < amountOfAdsToPlace &&
      cumulativeHeight < contentContainerHeight - bottomSafeArea
    ) {
      const nextSibling = el.nextElementSibling;
      // check if the current element has an immediate sibling
      if (nextSibling === null) {
        // there is no sibling, so it's safe to place an ad
        placeAd = true;
      } else {
        // check if the sibling tag is in the list of valid tags that indicate the start of a new section
        if (validSectionTags.includes(nextSibling.tagName.toLowerCase())) {
          // yes this tag is included in the list of valid tags, so let's place an ad
          placeAd = true;
        }
      }
    }
    if (placeAd === true) {
      // we are going to place the ad now
      heightCounter = 0;
      const currentAd = ads[amountOfAdsDisplayed];
      // place an ad here
      // FIX: you probably want to revert back to a react AdSlot object
      el.insertAdjacentHTML(
        'afterend',
        renderToString(
          <div className="adsInContent">
            <AdSlot
              id={currentAd?.id || ''}
              featureDisplayAdsFlag={featureDisplayAdsFlag}
            />
          </div>
        )
      );
      const { googletag, getAllAdSizes } = window;
      googletag.cmd.push(function () {
        googletag
          .defineSlot(
            currentAd.path,
            getAllAdSizes(currentAd.size),
            currentAd.id
          )
          .defineSizeMapping(currentAd.size)
          .addService(googletag.pubads());
        googletag.display(currentAd.id);
      });
      amountOfAdsDisplayed++;
    }
  };

  // now that we know what ad slots we need to show, let's define and display them all in one go
  immediateChildren.forEach(child => {
    if (amountOfAdsDisplayed >= amountOfAdsToPlace) {
      return;
    }
    const childHeight = outerHeight(child);

    // check if the height of this block is higher than the evenly distributed height
    // if it is, let's break it up in smaller pieces
    // only do this if it's a div without a class (sometimes the cms wraps divs around large content blocks)
    // set parseSubChildWithClassNames to true to also break up divs WITH a classname
    // set parseSubChildWithClassNames to false to only break up divs WITHOUT a classname (sometimes the cms wraps divs around large content blocks)
    if (
      childHeight > evenlyDistributedHeight &&
      child.tagName == 'DIV' &&
      (parseSubChildWithClassNames === true
        ? true
        : child.classList.length == 0)
    ) {
      // find all immediate subchildren in the div that potentially can be considered sections
      const subChildren = child.querySelectorAll(':scope > *');
      subChildren.forEach(subChild => {
        if (amountOfAdsDisplayed >= amountOfAdsToPlace) {
          return;
        }

        const subChildHeight = outerHeight(subChild);

        heightCounter += subChildHeight;
        cumulativeHeight += subChildHeight;

        evaluateAdPlacement(subChild);
      });
    } else {
      // add the current child height to the height counter
      heightCounter += childHeight;
      cumulativeHeight += childHeight;

      evaluateAdPlacement(child);
    }
  });
};

// function to insert vertical ad slots based on container height
export const insertVerticalAds = (
  ads = [],
  adsContainerSelector = '',
  mainContainerElSelector = '#app',
  averageAdHeight = 100,
  featureDisplayAdsFlag
) => {
  const adsContainerEl = document.querySelector(adsContainerSelector);
  const mainContainerEl = document.querySelector(mainContainerElSelector);

  // get the height of these containers
  const mainContainerHeight = mainContainerEl.offsetHeight;

  const maxNumberOfAds = Math.floor(
    (0.3 * mainContainerHeight) / averageAdHeight
  );
  const amountOfAdsToPlace =
    maxNumberOfAds > ads.length ? ads.length : maxNumberOfAds;

  const { googletag, getAllAdSizes } = window;
  // now that we know what ad slots we need to show, let's define and display them all in one go
  googletag.cmd.push(() => {
    for (let i = 0; i < amountOfAdsToPlace; i++) {
      const currentAd = ads[i];
      adsContainerEl.insertAdjacentHTML(
        'beforeend',
        renderToString(
          <AdSlot
            id={currentAd?.id || ''}
            className="adsInContent"
            featureDisplayAdsFlag={featureDisplayAdsFlag}
          />
        )
      );
      googletag
        .defineSlot(currentAd.path, getAllAdSizes(currentAd.size), currentAd.id)
        .defineSizeMapping(currentAd.size)
        .addService(googletag.pubads());
      googletag.display(currentAd.id);
    }
  });
};
